import type { FC } from 'react';
import type { GeneratedSpecial } from './SpecialCard';

interface StructuredSpecialCardProps {
  special: GeneratedSpecial | string;
}

export const StructuredSpecialCard: FC<StructuredSpecialCardProps> = ({ special }) => {
  // Extract the description that contains the structured format
  let description = '';
  let logoUrl = '';
  let imageUrl = '';

  // Handle different input types
  if (typeof special === 'string') {
    try {
      // Try to extract the JSON part
      const jsonStart = special.indexOf('{');
      const jsonEnd = special.lastIndexOf('}');
      
      if (jsonStart >= 0 && jsonEnd > jsonStart) {
        const jsonStr = special.substring(jsonStart, jsonEnd + 1);
        const parsedData = JSON.parse(jsonStr);
        
        description = parsedData.description || '';
        logoUrl = parsedData.logoUrl || '';
        imageUrl = parsedData.imageUrl || '';
      }
    } catch (e) {
      console.error('Failed to parse special JSON:', e);
    }
  } else if (special) {
    // It's already an object
    description = special.description || '';
    logoUrl = special.logoUrl || '';
    imageUrl = special.imageUrl || '';
  }

  // Function to convert plain text to HTML with proper formatting
  const formatStructuredText = (text: string) => {
    if (!text) return [];

    // Log the raw text for debugging
    console.log('Raw structured text:', text);

    // Split the text into sections
    const lines = text.split('\n');
    const formattedLines = lines.map((line, index) => {
      // Handle divider lines
      if (line.trim() === '⸻' || line.trim().match(/^-{3,}$/)) {
        return <hr key={`divider-${index}`} className="my-4 border-gray-300" />;
      }
      
      // Handle title/restaurant name (usually first line)
      if (index === 0 || (index <= 2 && line.trim().length > 0 && !line.includes('⸻'))) {
        return <h2 key={`title-${index}`} className="text-xl font-bold text-center mb-3">{line}</h2>;
      }
      
      // Handle headers (lines with emoji at the start)
      if (line.match(/^\s*[\p{Emoji}]/u)) {
        return <h3 key={`header-${index}`} className="text-lg font-bold mt-4 mb-2">{line}</h3>;
      }
      
      // Handle numbered items (1. 2. 3. etc)
      if (line.match(/^\s*\d+\.\s+/)) {
        return <h4 key={`item-${index}`} className="text-md font-semibold mt-3">{line}</h4>;
      }
      
      // Handle item descriptions (indented lines after numbered items)
      if (line.trim() && !line.match(/^\s*[•\-]/u) && !line.match(/^\s*\d+\./)) {
        return <p key={`desc-${index}`} className="text-sm text-gray-700 ml-6 mb-2">{line}</p>;
      }
      
      // Handle bullet points
      if (line.match(/^\s*[•\-]/u)) {
        return <p key={`bullet-${index}`} className="text-sm ml-6 mb-1">{line}</p>;
      }
      
      // Handle section headers (like "Add-Ons & Perks")
      if (line.includes('Add-Ons') || line.includes('Perks') || line.includes('SPECIAL') || line.toUpperCase() === line) {
        return <h3 key={`section-${index}`} className="text-lg font-semibold mt-4 mb-2">{line}</h3>;
      }
      
      // Default case
      if (line.trim()) {
        return <p key={`line-${index}`} className="mb-1">{line}</p>;
      }
      
      // Empty lines become spacing
      return <div key={`space-${index}`} className="h-2"></div>;
    });

    return formattedLines;
  };

  return (
    <div className="bg-white rounded-xl shadow-md p-6 max-w-md mx-auto my-6">
      {logoUrl && (
        <div className="flex justify-center mb-4">
          <img 
            src={logoUrl} 
            alt="Restaurant Logo" 
            className="h-10"
            loading="lazy"
          />
        </div>
      )}
      
      {imageUrl && (
        <img 
          src={imageUrl} 
          alt="Special"
          className="w-full rounded-lg mb-4 object-cover h-48"
          loading="lazy"
        />
      )}
      
      <div className="structured-special prose prose-sm mx-auto whitespace-pre-line">
        {formatStructuredText(description)}
      </div>
    </div>
  );
};
